import { FC, useState } from 'react';

import { DocumentOrderLineData, InvoiceResponse, OrderDocumentType } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

interface InvoiceContentProps {
   data: InvoiceResponse;
   onDownload: (document: InvoiceResponse, orderLineData: DocumentOrderLineData, type: OrderDocumentType) => void;
   onViewDocument: (document: InvoiceResponse, orderLineData: DocumentOrderLineData) => void;
}

export const InvoiceContent: FC<InvoiceContentProps> = ({ data, onDownload, onViewDocument }) => {
   const translate = useTranslations();
   const format = useFormatter();
   const [isOrderExpanded, setIsOrderExpanded] = useState(false);
   const { phone, tabPort } = useBreakpoint();
   const filteredOrderLineData = isOrderExpanded ? data.orderLineData : data.orderLineData.slice(0, 1);

   return (
      <Box gap="sm" direction="column" width="100%">
         {filteredOrderLineData.map((orderLine, index) => (
            <Box direction={tabPort || phone ? 'column' : 'row'} gap="md" key={orderLine.orderNumber} width="100%">
               {index > 0 && (phone || tabPort) && <Divider />}
               <Box direction="column" gap="none" width={tabPort || phone ? '100%' : '30%'} wrap="wrap" justify="center">
                  <Typography variant="body-highlight" component="span">
                     {orderLine.productDescription}
                  </Typography>
                  <Typography variant="small" component="span" color="subtle">
                     {`${translate('shipment.documents.order')} ${orderLine.orderNumber}`}
                  </Typography>
               </Box>
               <Box direction="column" gap="none" width={tabPort || phone ? '100%' : '20%'}>
                  {index < 1 && (
                     <>
                        <Typography variant="small" component="span" color="subtle">
                           {translate('shipment.documents.invoice-type')}
                        </Typography>
                        <Typography variant="small" component="span">
                           {data.type}
                        </Typography>
                     </>
                  )}
               </Box>
               <Box direction="column" gap="none" width={tabPort || phone ? '100%' : '20%'}>
                  {index < 1 && (
                     <>
                        <Typography variant="small" component="span" color="subtle">
                           {translate('shipment.documents.invoice-number')}
                        </Typography>
                        <Typography variant="small" component="span">
                           {data.number}
                        </Typography>
                     </>
                  )}
               </Box>
               <Box gap="none" width={tabPort || phone ? '100%' : '30%'}>
                  <Box direction="column" gap="none" width={tabPort || phone ? '50%' : '100%'}>
                     {index < 1 && (
                        <>
                           <Typography variant="small" component="span" color="subtle">
                              {translate('shipment.documents.invoice-date')}
                           </Typography>
                           <Typography variant="small" component="span">
                              {format.dateTime(data.createdOn)}
                           </Typography>
                        </>
                     )}
                  </Box>
                  <Box items="start" justify="end" width={tabPort || phone ? '50%' : '100%'} gap="xs" direction="row">
                     {index < 1 && (
                        <>
                           <IconButton
                              icon="download"
                              onClick={() => onDownload(data, data.orderLineData[0], 'Invoice')}
                              type="button"
                              variant="on-light"
                           />
                           <IconButton icon="eye" onClick={() => onViewDocument(data, data.orderLineData[0])} type="button" variant="on-light" />
                        </>
                     )}
                  </Box>
               </Box>
            </Box>
         ))}
         <Box justify="start">
            {data.orderLineData.length > 1 && (
               <Button
                  variant="tertiary"
                  onClick={() => setIsOrderExpanded(!isOrderExpanded)}
                  trailingIcon={isOrderExpanded ? 'chevron-up' : 'chevron-down'}
               >
                  {isOrderExpanded
                     ? translate('shipment.documents.show-less')
                     : `${data.orderLineData.length - 1} ${translate('shipment.documents.show-more')} `}
               </Button>
            )}
         </Box>
         {phone && filteredOrderLineData.length > 1 && <Divider />}
      </Box>
   );
};
